import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
} from '@mui/material';
import { useRejectPair } from '../api/usePairMapper';
import { useFixMatch } from '../api/useEventAgent';

interface Props {
  pairId: string | null;
  pairName?: string;
  onClose: () => void;
  /** Called with the fix-match run id when the operator gave a reason. */
  onFixMatchStarted: (runId: string) => void;
  /**
   * Called with the pairId after the pair is successfully unmatched. Lets a
   * context-driven caller (e.g. the Opportunities table) drop the row — the
   * dialog's own react-query invalidation only covers the mapper's pair list.
   */
  onUnmatched?: (pairId: string) => void;
  /** Called when the background unmatch/fix-match sequence throws. */
  onError?: (error: unknown) => void;
}

/**
 * Unmatch a pair. Reason-gated: an empty reason is a plain unmatch; a filled
 * reason additionally dispatches a fix-match agent run that reviews the
 * matcher and (only for clear cases) opens a draft matching-core PR.
 *
 * The dialog closes the moment the operator confirms — both API calls run as
 * an unawaited background promise so the Opportunities page never blocks.
 * Order is forced: fix-match must precede reject because the backend snapshots
 * the pair before reject deletes it. Errors surface via `onError`.
 */
export const RejectPairDialog = ({
  pairId,
  pairName,
  onClose,
  onFixMatchStarted,
  onUnmatched,
  onError,
}: Props) => {
  const [reason, setReason] = useState('');
  const rejectPair = useRejectPair();
  const fixMatch = useFixMatch();
  const hasReason = reason.trim().length > 0;

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleUnmatch = () => {
    if (!pairId) return;
    const id = pairId;
    const trimmed = reason.trim();
    const withReason = trimmed.length > 0;
    handleClose();
    if (withReason) {
      // Background sequence — modal is already closed. fix-match FIRST because
      // the backend snapshots the pair before reject deletes it.
      void (async () => {
        try {
          const run = await fixMatch.mutateAsync({ pairId: id, reason: trimmed });
          await rejectPair.mutateAsync(id);
          onUnmatched?.(id);
          onFixMatchStarted(run._id);
        } catch (err) {
          console.error('Background unmatch + fix-match failed:', err);
          onError?.(err);
        }
      })();
    } else {
      void (async () => {
        try {
          await rejectPair.mutateAsync(id);
          onUnmatched?.(id);
        } catch (err) {
          console.error('Background unmatch failed:', err);
          onError?.(err);
        }
      })();
    }
  };

  return (
    <Dialog open={!!pairId} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Unmatch pair</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {pairName && (
            <Typography variant="body2" color="text.secondary">
              {pairName}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            The pair is unlinked and won't re-match. Optionally say <em>why</em>{' '}
            the match was wrong — that dispatches the fix-match agent, which
            reviews the matcher and, only for clear cases, opens a draft PR for
            you to review. Both run in the background — this dialog closes
            immediately.
          </Typography>
          <TextField
            label="Why was this match wrong? (optional)"
            placeholder="e.g. paired O/U 9.5 corners to Kalshi 9+ — should be 10+"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleUnmatch}
          disabled={!pairId}
        >
          {hasReason ? 'Unmatch & review matcher' : 'Unmatch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
